import { useEffect } from 'react';
import type { MailAttachment } from '@jerome2/shared';
import { fileSize, isViewable } from '../lib/format.ts';
import { attachmentUrl } from './html.ts';
import { Icon } from '../ui/Icon.tsx';
import { Button, IconButton } from '../ui/primitives.tsx';

// Paneel over de lezer voor PDF en afbeeldingen; andere bestanden worden gedownload. Een pdf heeft
// ook Openen (nieuw tabblad): op iOS toont een iframe alleen de eerste pagina, in een eigen tabblad
// werkt de pdf-weergave van Safari wel. Knoppen op aanraakschermen 44 px.

const linkClass = 'inline-flex items-center gap-1.5 h-8 pointer-coarse:h-11 px-3 rounded-md text-sm text-ink-soft hover:bg-tint';

export function AttachmentViewer(props: { messageId: string; attachment: MailAttachment; onClose: () => void }) {
  const kind = isViewable(props.attachment.name, props.attachment.contentType);
  const url = attachmentUrl(props.messageId, props.attachment.id);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        props.onClose();
      }
    };
    document.addEventListener('keydown', onKey, true);
    return () => document.removeEventListener('keydown', onKey, true);
  }, [props]);
  return (
    <div className="absolute inset-0 z-20 bg-surface flex flex-col" role="dialog" aria-label={props.attachment.name}>
      <div className="h-12 shrink-0 flex items-center gap-2 px-3 border-b border-line">
        <Icon name="file" size={16} className="text-muted" />
        <span className="font-medium truncate">{props.attachment.name}</span>
        <span className="text-[13px] text-muted">{fileSize(props.attachment.size)}</span>
        <div className="ml-auto flex items-center gap-1">
          {kind === 'pdf' ? (
            <a href={url} target="_blank" rel="noopener noreferrer" className={linkClass}>
              <Icon name="external" size={15} /> Openen
            </a>
          ) : null}
          <a href={attachmentUrl(props.messageId, props.attachment.id, true)} download={props.attachment.name} className={linkClass}>
            <Icon name="download" size={15} /> Downloaden
          </a>
          <IconButton icon="x" label="Sluiten (Esc)" onClick={props.onClose} className="pointer-coarse:h-11 pointer-coarse:w-11" />
        </div>
      </div>
      <div className="flex-1 min-h-0 bg-paper">
        {kind === 'pdf' ? (
          <iframe title={props.attachment.name} src={url} className="w-full h-full border-0" />
        ) : kind === 'image' ? (
          <div className="w-full h-full overflow-auto flex items-center justify-center p-4">
            <img src={url} alt={props.attachment.name} className="max-w-full max-h-full object-contain shadow-card" />
          </div>
        ) : (
          <div className="h-full flex items-center justify-center text-center p-6">
            <div>
              <p className="text-ink mb-3">Dit bestand kan Jerome niet in beeld tonen.</p>
              <a href={attachmentUrl(props.messageId, props.attachment.id, true)} download={props.attachment.name}>
                <Button variant="primary" icon="download">Downloaden</Button>
              </a>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
